export function OnboardingPolicyService($http, apexApiUrl) {
    'ngInject'
    return {
        retrieveOnboardingPolicy: retrieveOnboardingPolicy,
        createOnboardingPolicy: createOnboardingPolicy,
        retrieveDivisionOnboardingPolicy: retrieveDivisionOnboardingPolicy,
        defineDivisionOnboardingPolicy: defineDivisionOnboardingPolicy,
        removeDivisionOnboardingPolicy: removeDivisionOnboardingPolicy,
        patchOnboardingPolicy: patchOnboardingPolicy,
        updateContactEmails: updateContactEmails,
        addApplicationStatus: addApplicationStatus,
        updateApplicationStatus: updateApplicationStatus,
        addReferenceField: addReferenceField,
        updateReferenceField: updateReferenceField,
        addDocumentGroup: addDocumentGroup,
        updateDocumentGroup: updateDocumentGroup,
        uploadDocumentGroupRequirementsFile: uploadDocumentGroupRequirementsFile,
        removeDocumentGroupRequirementsFile: removeDocumentGroupRequirementsFile,
        addDocument: addDocument,
        updateDocument: updateDocument,
        uploadDocumentBlankForm: uploadDocumentBlankForm,
        removeDocumentBlankForm: removeDocumentBlankForm
    };

    /**
     * Retrieve the onboarding policy with the given ID.
     * @param {any} id The onboarding policy's primary id.
     * @returns {Promise<any>} The resolved onboarding policy.
     */
    function retrieveOnboardingPolicy(id) {
        return $http({
            method: "GET",
            url: apexApiUrl + "onboarding-policies/" + id,
            headers: { Accept: "application/json" }
        })
            .then(function (response) {
                setOnboardingPolicyReferences(response.data);
                return response.data;
            });
    }

    /**
     * Retrieve the onboarding policy associated with the given division.
     * @param {any} divisionID The division's primary key.
     */
    function retrieveDivisionOnboardingPolicy(divisionID) {
        return $http({
            method: "GET",
            url: apexApiUrl + "divisions/" + divisionID + "/onboarding-policy",
            headers: { Accept: "application/json" }
        })
            .then(function (response) {
                setOnboardingPolicyReferences(response.data);
                return response.data;
            });
    }

    /**
     * Create a new onboarding policy with the given with request data.
     * @param {any} request
     */
    function createOnboardingPolicy(request) {
        const formData = mapOnboardingPolicyCreateRequestToFormData(request);
        return $http({
            method: "POST",
            url: apexApiUrl + "onboarding-policies",
            headers: { "Content-Type": undefined, Accept: "application/json" },
            data: formData
        })
            .then(function (response) {
                setOnboardingPolicyReferences(response.data);
                return response.data;
            });
    }

    /**
     * Change the given onboarding policy's name or self-sign-up settings.
     * @param {any} onboardingPolicyID The ID for the policy
     * @param {{ Name : String, EnableSelfSignUp : boolean }} request The updated policy fields.
     * @returns {Promise} async
     */
    function patchOnboardingPolicy(onboardingPolicyID, request) {
        return $http({
            method: "PATCH",
            url: apexApiUrl + "onboarding-policies/" + onboardingPolicyID,
            headers: { "Content-Type": "application/json" },
            data: request
        });
    }

    /**
     * Create a new onboarding policy that is defined as the given division's onboarding policy.
     * @param {any} divisionID
     * @param {any} onboardingPolicy
     */
    function defineDivisionOnboardingPolicy(divisionID, onboardingPolicy) {
        const formData = mapOnboardingPolicyCreateRequestToFormData(onboardingPolicy);
        return $http({
            method: "PUT",
            url: apexApiUrl + "divisions/" + divisionID + "/onboarding-policy",
            headers: { "Content-Type": undefined, Accept: "application/json" },
            data: formData
        })
            .then(function (response) {
                return retrieveDivisionOnboardingPolicy(divisionID);
            })
            .then(function (policy) {
                setOnboardingPolicyReferences(policy);
                return policy;
            });
    }

    /**
     * Disassociate the given division from the current onboarding policy that is defined for it.
     * @param {any} divisionID
     */
    function removeDivisionOnboardingPolicy(divisionID) {
        return $http({
            method: "DELETE",
            url: apexApiUrl + "divisions/" + divisionID + "/onboarding-policy"
        });
    }

    /**
     * Update the list of contact emails for the given onboarding policy.
     * @param {any} onboardingPolicyID The primary key for the onboarding policy.
     * @param {string[]} contactEmails The list of contact emails to set for the policy.
     */
    function updateContactEmails(onboardingPolicyID, contactEmails) {
        return $http({
            method: "PUT",
            url: apexApiUrl + "onboarding-policies/" + onboardingPolicyID + "/contact-emails",
            data: contactEmails
        })
    }

    /**
     * Add a new application status to a specific index in the onboarding policy.
     * @param {any} onboardingPolicyID
     * @param {any} request A request containing the application status details and index.
     */
    function addApplicationStatus(onboardingPolicyID, request) {
        return $http({
            method: "POST",
            url: apexApiUrl + "onboarding-policies/" + onboardingPolicyID + "/application-statuses",
            data: request
        }).then(function (response) {
            return response.data;
        });
    }

    /**
     * Add a new application status to a specific index in the onboarding policy.
     * @param {Number} onboardingPolicyID
     * @param {Number} id The id of the application status to be editing.
     * @param {any} request A request containing the application status details and index.
     */
    function updateApplicationStatus(onboardingPolicyID, id, request) {
        return $http({
            method: "PUT",
            url: apexApiUrl + "onboarding-policies/" + onboardingPolicyID + "/application-statuses/" + id,
            data: request
        });
    }

    /**
     * Add a new reference field to the given onboarding policy.
     * @param {number} onboardingPolicyID
     * @param {any} request
     */
    function addReferenceField(onboardingPolicyID, request) {
        return $http({
            method: "POST",
            url: apexApiUrl + "onboarding-policies/" + onboardingPolicyID + "/reference-fields",
            data: request
        }).then(function (response) {
            return response.data;
        });
    }

    /**
     * Update the reference field of the onboarding policy at the given index.
     * @param {number} onboardingPolicyID
     * @param {number} index
     * @param {any} request
     */
    function updateReferenceField(onboardingPolicyID, id, request) {
        return $http({
            method: "PUT",
            url: apexApiUrl + "onboarding-policies/" + onboardingPolicyID + "/reference-fields/" + id,
            data: request
        });
    }

    /**
     * Add a new document group to the onboarding policy.
     * @param {number} onboardingPolicyID
     * @param {any} request
     */
    function addDocumentGroup(onboardingPolicyID, request) {
        const formData = new FormData();
        appendAddDocumentGroupRequestToFormData(formData, request);
        formData.append("SortIndex", request.SortIndex);
        return $http({
            method: "POST",
            url: apexApiUrl + "onboarding-policies/" + onboardingPolicyID + "/document-groups",
            data: formData,
            headers: { "Content-Type": undefined, Accept: "application/json" }
        }).then(function (response) {
            return response.data;
        });
    }

    /**
     * Update the document group at the given index.
     * @param {number} onboardingPolicyID
     * @param {number} id
     * @param {any} request
     */
    function updateDocumentGroup(onboardingPolicyID, id, request) {
        return $http({
            method: "PUT",
            url: apexApiUrl + "onboarding-policies/" + onboardingPolicyID + "/document-groups/" + id,
            data: request
        });
    }

    /**
     * Upload a file representing the requirements file template for the document group.
     * @param {number} onboardingPolicyID
     * @param {number} index
     * @param {any} requirementsFile
     */
    function uploadDocumentGroupRequirementsFile(onboardingPolicyID, id, requirementsFile) {
        const formData = new FormData();
        formData.append("RequirementsFileUpload", requirementsFile);
        return $http({
            method: "PUT",
            url: apexApiUrl + "onboarding-policies/" + onboardingPolicyID + "/document-groups/" + id + "/" + "requirements-file",
            data: formData,
            headers: { "Content-Type": undefined, Accept: "application/json" }
        });
    }

    /**
     * Remove the uploaded requirements file from the document group.
     * @param {any} onboardingPolicyID
     * @param {any} index
     */
    function removeDocumentGroupRequirementsFile(onboardingPolicyID, id) {
        return $http({
            method: "DELETE",
            url: apexApiUrl + "onboarding-policies/" + onboardingPolicyID + "/document-groups/" + id + "/" + "requirements-file"
        });
    }

    /**
     * Add a new document field to the onboarding policy.
     * @param {any} onboardingPolicyID
     * @param {any} request
     */
    function addDocument(onboardingPolicyID, request) {
        const formData = new FormData();
        appendAddDocumentRequestToFormData(formData, request);
        formData.append("SortIndex", request.SortIndex);
        return $http({
            method: "POST",
            url: apexApiUrl + "onboarding-policies/" + onboardingPolicyID + "/documents",
            data: formData,
            headers: { "Content-Type": undefined, Accept: "application/json" }
        }).then(function (response) {
            return response.data;
        });
    }

    /**
     * Update the document field at the given index in the onboarding policy.
     * @param {any} onboardingPolicyID
     * @param {any} index
     * @param {any} request
     */
    function updateDocument(onboardingPolicyID, id, request) {
        return $http({
            method: "PUT",
            url: apexApiUrl + "onboarding-policies/" + onboardingPolicyID + "/documents/" + id,
            data: request
        });
    }

    /**
     * Upload a blank file template for the given document field.
     * @param {any} onboardingPolicyID
     * @param {any} index
     * @param {any} blankForm
     */
    function uploadDocumentBlankForm(onboardingPolicyID, id, blankForm) {
        const formData = new FormData();
        formData.append("BlankFormUpload", blankForm);
        return $http({
            method: "PUT",
            url: apexApiUrl + "onboarding-policies/" + onboardingPolicyID + "/documents/" + id + "/" + "blank-form",
            data: formData,
            headers: { "Content-Type": undefined, Accept: "application/json" }
        });
    }

    /**
     * Remove the blank file for the given document field.
     * @param {any} onboardingPolicyID
     * @param {any} index
     */
    function removeDocumentBlankForm(onboardingPolicyID, id) {
        return $http({
            method: "DELETE",
            url: apexApiUrl + "onboarding-policies/" + onboardingPolicyID + "/documents/" + id + "/" + "blank-form"
        });
    }

    function mapOnboardingPolicyCreateRequestToFormData(onboardingPolicy) {
        // Need to serialize as form data manually.
        const formData = new FormData();
        onboardingPolicy.ContactEmails.forEach(function (contactEmail, i) {
            formData.append("ContactEmails[" + i + "]", contactEmail);
        });
        onboardingPolicy.ApplicationStatuses.forEach(function (status, i) {
            formData.append("ApplicationStatuses[" + i + "].Name", status.Name);
            formData.append("ApplicationStatuses[" + i + "].StatusType", status.StatusType || "null");
        });
        onboardingPolicy.ReferenceFields.forEach(function (referenceField, i) {
            const base = "ReferenceFields[" + i + "]";

            formData.append(base + ".Name", referenceField.Name);
            formData.append(base + ".RequiredAtStatus", referenceField.RequiredAtStatus);
            formData.append(base + ".IsSensitive", referenceField.IsSensitive || "false");
            formData.append(base + ".InputMask", referenceField.InputMask);
            formData.append(base + ".ValueType", referenceField.ValueType);
            formData.append(base + ".ShowInGrid", referenceField.ShowInGrid || "false");
        });
        onboardingPolicy.OnboardingDocumentGroups.forEach(function (group, i) {
            appendAddDocumentGroupRequestToFormData(formData, group, "OnboardingDocumentGroups[" + i + "].");
        });
        onboardingPolicy.OnboardingDocuments.forEach(function (document, i) {
            appendAddDocumentRequestToFormData(formData, document, "OnboardingDocuments[" + i + "].");
            formData.append("OnboardingDocuments[" + i + "].DocumentGroup", document.DocumentGroup);
        });
        formData.append("EnableSelfSignUp", String(!!onboardingPolicy.EnableSelfSignUp));
        return formData;
    }

    function appendAddDocumentGroupRequestToFormData(formData, documentGroup, prefix) {
        prefix = prefix || "";
        formData.append(prefix + "Name", documentGroup.Name);
        formData.append(prefix + "RequirementsFileUpload", documentGroup.RequirementsFileUpload);
    }

    function appendAddDocumentRequestToFormData(formData, document, prefix) {
        prefix = prefix || "";
        formData.append(prefix + "Name", document.Name);
        formData.append(prefix + "Description", document.Description);
        formData.append(prefix + "DocumentGroupID", document.DocumentGroupID || "null");
        formData.append(prefix + "IsRequired", document.IsRequired || "false");
        formData.append(prefix + "UseImageCropper", document.UseImageCropper || "false");
        document.AllowedFileTypes.forEach(function (type, j) {
            formData.append(prefix + "AllowedFileTypes[" + j + "]", type);
        });
        formData.append(prefix + "BlankFormUpload", document.BlankFormUpload);
    }

    function setOnboardingPolicyReferences(policy) {
        // Set references between the policy's statuses and document groups to the policy's list of these objects.
        policy.OnboardingDocuments.forEach(function (document) {
            if (document.OnboardingPolicyDocumentGroupID) {
                document.DocumentGroup = policy.OnboardingDocumentGroups.filter(function (group) {
                    return group.OnboardingPolicyDocumentGroupID
                        === document.OnboardingPolicyDocumentGroupID;
                })[0];

            }
        })
        policy.ReferenceFields.forEach(function (reference) {
            if (reference.RequiredAtStatus) {
                reference.RequiredAtStatus = policy.ApplicationStatuses.filter(function (status) {
                    return status.OnboardingApplicationStatusID
                        === reference.RequiredAtStatus.OnboardingApplicationStatusID;
                })[0];
            }
        })
    }
}
